import { ButtonHTMLAttributes, forwardRef, ReactNode } from 'react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  loading?: boolean;
};

const VARIANT: Record<Variant, string> = {
  primary: 'bg-primary text-white hover:bg-primary-dark border-transparent shadow-sm',
  secondary: 'bg-surface text-primary-text hover:bg-canvas-soft shadow-marked border-buttonBorder hover:border-buttonBorderHover',
  ghost: 'bg-transparent text-secondary hover:text-primary-text hover:bg-canvas-soft border-transparent',
  danger: 'bg-rose-600 text-white hover:bg-rose-700 border-transparent shadow-sm',
};

// Compact scale: font sizes stay put, heights/padding one step tighter.
const SIZE: Record<Size, string> = {
  sm: 'h-[28px] px-3 text-[0.75rem] rounded-[8px]',
  md: 'h-[32px] px-3.5 text-[0.8125rem] rounded-[10px]',
  lg: 'h-[40px] px-5 text-[0.875rem] rounded-[10px]',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', leftIcon, rightIcon, loading, className = '', children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={`inline-flex items-center justify-center gap-2 border font-medium tracking-tight transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed ${VARIANT[variant]} ${SIZE[size]} ${className}`}
      {...rest}
    >
      {loading ? <span className="inline-block w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : leftIcon}
      {children}
      {rightIcon}
    </button>
  );
});
